import { Button, IconButton, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadFile } from '@mui/icons-material';

import { logger } from '@common/logging/logger';
import { PropertiesTitle } from '@/modules/common/components/PropertiesTitle';
import { useFileTypeValidator } from '@/modules/common/hooks/useFileValidator';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';
import { StyledTooltip } from '@/modules/common/components/StyledTooltip';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { FileServiceConnector } from '@/modules/api/FileApiConnector';
import { PROJECT_FILES_ROOT_CONTAINER, ORDER_PROFILE_CONTAINER } from '@/modules/api/constant';
import AbortIcon from '@/assets/icons/AbortIcon';

import { SimulationUtil } from '../../SimulationUtil';
import FileUploadStatus from './FileStatus';

interface Props {
  customOrderProfileUrl?: string;
  onUploadOrderProfile: (filePath: string) => void;
  onRemoveUploadedOrderProfile: () => void;
}

export const UploadOrderProfile = ({
  customOrderProfileUrl,
  onUploadOrderProfile,
  onRemoveUploadedOrderProfile,
}: Props) => {
  const { t } = useTranslation('interface');
  const { validateFileType } = useFileTypeValidator();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSnackbar } = useSnackbarStore();
  const [fileUploaded, setFileUploaded] = useState<boolean>(!!customOrderProfileUrl);

  const buildOrderProfileFileRef = (fileName: string) => `${ORDER_PROFILE_CONTAINER}/${fileName}`;

  const uploadOrderProfileFileToFileStorage = useCallback(
    async (file: Blob, fileRef: string, extension: 'csv' | 'json') => {
      try {
        const basePath = buildRevisionFileStorageBasePath();
        if (!basePath) throw Error('basePath not defined.');

        const fileApi = FileServiceConnector.getInstance();
        await fileApi.addFile(
          `${basePath}/${fileRef}`,
          file,
          extension,
          PROJECT_FILES_ROOT_CONTAINER,
        );
      } catch (error) {
        logger.error('Error uploading order profile to file storage', error);
        return { success: false };
      }

      return { success: true };
    },
    [],
  );

  const handleFileSelection = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const orderProfileFile = event.target.files?.[0];

      if (!orderProfileFile || !validateFileType(orderProfileFile, ['csv', 'json'])) {
        return;
      }

      const reader = new FileReader();
      reader.onload = async () => {
        const content = await orderProfileFile.text();

        try {
          const ext = orderProfileFile.name.split('.').pop();
          const orderProfileFileRef = buildOrderProfileFileRef(orderProfileFile.name);

          const fileUploadResult = await uploadOrderProfileFileToFileStorage(
            new Blob([content], { type: orderProfileFile.type }),
            orderProfileFileRef,
            ext as 'csv' | 'json',
          );

          if (!fileUploadResult.success) {
            showSnackbar(t('errors:files.upload_failed'));
          }
          onUploadOrderProfile(orderProfileFileRef);
          setFileUploaded(true);
        } catch (error) {
          logger.error('Error running simulation with uploaded order profile', error);
        }
      };

      reader.readAsText(orderProfileFile);
    },
    [validateFileType, uploadOrderProfileFileToFileStorage, onUploadOrderProfile, showSnackbar, t],
  );

  const removeUploadedOrderProfile = useCallback(() => {
    onRemoveUploadedOrderProfile();
    setFileUploaded(false);
  }, [onRemoveUploadedOrderProfile]);

  const selectCustomOrderProfile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  return (
    <Stack spacing={1} mb={2}>
      <PropertiesTitle
        fontSize={14}
        fontWeight={400}
        value={t('simulation.experimental_settings.upload_transport_orders.title')}
      />
      <Typography variant='body2' sx={{ fontSize: '10px' }}>
        {t('simulation.experimental_settings.upload_transport_orders.helper_text')}
      </Typography>
      <Stack
        justifyContent='center'
        alignItems='center'
        flexDirection='row'
        gap={1}
        sx={{ width: '100%' }}
      >
        <FileUploadStatus
          fileUploaded={fileUploaded}
          fileName={SimulationUtil.getFileNameFromUrl(customOrderProfileUrl || '')}
        />
        <StyledTooltip
          disabled={!customOrderProfileUrl}
          title={t('simulation.experimental_settings.remove_file')}
        >
          <IconButton
            sx={{ p: 0, mr: '3px' }}
            disabled={!customOrderProfileUrl}
            onClick={removeUploadedOrderProfile}
          >
            <AbortIcon />
          </IconButton>
        </StyledTooltip>
      </Stack>
      <Button
        onClick={selectCustomOrderProfile}
        sx={{
          height: 40,
          padding: 0,
          justifyContent: 'flex-start',
          fontSize: 10,
          textTransform: 'none',
          border: 0,
          '&:hover': { color: 'primary.main', backgroundColor: 'transparent' },
        }}
        endIcon={<UploadFile color='primary' />}
      >
        {t('simulation.experimental_settings.upload_transport_orders.button')}
      </Button>

      <input
        type='file'
        accept='.csv, .json'
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileSelection}
        hidden
      />
    </Stack>
  );
};
